"use client";
import React from "react";

import handMobileAr from "../../../public/handMobAr.png";
import handMobile from "../../../public/handMobAr.png";
import handMobileEn from "../../../public/handMobEn.png";
import googlePlay from "@/assets/icons/NewHomePage/googlePlay.png";
import appStore from "@/assets/icons/NewHomePage/appleStore.png";

import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useLanguage } from "@/contexts/LanguageContext";
import { GoldenTicketIcon } from "./GoldenTicketIcon";
import { EasyExperienceIcon } from "./EasyExperienceIcon";

import { AppStoreSvgEn } from "./AppStoreSvgEn";
import { GooglePlaySvgEn } from "./GooglePlaySvgEn";

import { GoldenTicketIconEn } from "./GoldenTicketIconEn";

export default function AppDownload({ init }: { init?: any }) {
  const t = useTranslations();
  const { language } = useLanguage();
  const isRTL = language === "ar";

  return (
    <div className="container mx-auto px-4 mb-[10px] w-full h-full">
      <div className="relative w-full h-full z-0">
        {/* Desktop Background */}
        <div
          className="hidden lg:block w-full h-[518px] rounded-[32px] backdrop-blur-sm shadow-2xs z-[-2]"
          style={{
            background: "rgba(255, 255, 255, 0.11)",
            boxShadow:
              "inset 0px 2px 0px rgba(209, 211, 212, 0.35), inset 0px -2px 0px rgba(209, 211, 212, 0.35)",
          }}
        ></div>
        {/* Mobile Background */}
        <div
          className="block lg:hidden w-full h-[254px] md:h-[400px] rounded-[32px] backdrop-blur-sm z-[-2]"
          style={{
            background: "rgba(255, 255, 255, 0.11)",
            boxShadow:
              "inset 0px 2px 0px rgba(209, 211, 212, 0.35), inset 0px -2px 0px rgba(209, 211, 212, 0.35)",
          }}
        ></div>

        {/* Desktop Hand Image */}
        <Image
          src={isRTL ? handMobileAr : handMobileEn}
          alt="handMobile"
          className={`h-[182%] w-[42.7%] hidden lg:block absolute bottom-0 ${!isRTL ? "left-0" : "right-0"} object-cover`}
        />

        {!isRTL ? (
          /* English (LTR) Layout - Mirrored X position on Desktop */
          <div className="absolute w-[80%] lg:w-[56%] top-[50%] lg:left-[75%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex flex-col justify-center items-center lg:items-start gap-2.5 lg:gap-6">
            <div className="flex items-center justify-center lg:justify-start gap-3 w-full">
              <h2 className="text-[18px] sm:text-[24px] lg:text-[42px] xl:text-[52px] font-black text-white leading-tight text-center lg:text-start tracking-tight">
                {t("newHome.goldenTicketAwaits")}
              </h2>
              <GoldenTicketIconEn className="lg:w-[84px] lg:h-[81px] w-[37px] h-[38px] shrink-0" />
            </div>

            <div className="flex flex-col justify-center lg:justify-start items-center lg:items-start text-center lg:text-start w-full">
              <p className="text-center lg:text-start text-[#D1D3D4] lg:text-[22px] xl:text-[28px] text-[14px] font-semibold leading-normal">
                Discover exciting events, book your seat, and
              </p>
              <p className="text-center lg:text-start text-[#D1D3D4] lg:text-[22px] xl:text-[28px] text-[14px] font-semibold flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-2 mt-1">
                <span>enjoy a seamless experience with</span>
                <EasyExperienceIcon className="lg:w-[150px] lg:h-[55px] w-[140px] h-[35px] shrink-0 mt-2 lg:mt-0" />
              </p>
            </div>

            <div className="flex justify-center lg:justify-start items-center gap-4 mt-2 lg:mt-10 w-full">
              <Link href={init?.apps?.apple_store || ""}>
                <AppStoreSvgEn className="xl:w-[151px] xl:h-[53px] lg:w-[120px] lg:h-[42px] w-[109px] h-[35px]" />
              </Link>
              <Link href={init?.apps?.google_play || ""}>
                <GooglePlaySvgEn className="xl:w-[151px] xl:h-[53px] lg:w-[120px] lg:h-[42px] w-[109px] h-[35px]" />
              </Link>
            </div>
          </div>
        ) : (
          /* Arabic (RTL) Layout */
          <div className="absolute w-[80%] lg:w-[56%] top-[50%] lg:left-[25%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex flex-col justify-center lg:items-start items-center gap-2.5 lg:gap-6">
            <div className="flex justify-center lg:justify-start items-center gap-3 w-full">
              <h2 className="text-[18px] sm:text-[24px] lg:text-[42px] xl:text-[52px] font-black text-white leading-tight tracking-tight">
                {t("newHome.goldenTicketAwaits")}
              </h2>
              <GoldenTicketIconEn className="lg:w-[84px] lg:h-[81px] w-[37px] h-[38px] shrink-0" />
            </div>

            <div className="flex flex-col justify-center items-center lg:items-start">
              <p className="text-center lg:text-start text-[#D1D3D4] lg:text-[24px] xl:text-[30px] text-[14px] font-medium">
                {t("newHome.discoverEvents")}{" "}
              </p>
              <p className="text-center lg:text-start text-[#D1D3D4] lg:text-[24px] xl:text-[30px] text-[14px] font-medium flex flex-col lg:flex-row justify-center lg:justify-start gap-2 items-center lg:items-center">
                <span>{t("newHome.easyExperienceWith")}</span>
                <EasyExperienceIcon className="lg:w-[150px] lg:h-[55px] w-[140px] h-[35px] mt-2 mb-[20px]" />
              </p>
            </div>

            <div className="flex justify-center lg:justify-start items-center gap-4 mt-0 lg:mt-3 w-full">
              <Link href={init?.apps?.apple_store || ""}>
                <Image
                  src={appStore}
                  alt="appStore"
                  className="xl:w-[151px] xl:h-[53px] lg:w-[120px] lg:h-[42px] w-[109px] h-[35px]"
                />
              </Link>
              <Link href={init?.apps?.google_play || ""}>
                <Image
                  src={googlePlay}
                  alt="googlePlay"
                  className="xl:w-[151px] xl:h-[53px] lg:w-[120px] lg:h-[42px] w-[109px] h-[35px]"
                />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
